import { motion } from "framer-motion";
import styled, { keyframes } from "styled-components";

// Enhanced Keyframe animations
export const slideInDown = keyframes`
  from { 
    transform: translateY(-80px) scale(0.95); 
    opacity: 0; 
  }
  to { 
    transform: translateY(0) scale(1); 
    opacity: 1; 
  }
`;

export const slideInUp = keyframes`
  from { 
    transform: translateY(60px) scale(0.95); 
    opacity: 0; 
  }
  to { 
    transform: translateY(0) scale(1); 
    opacity: 1; 
  }
`;

export const fadeInUp = keyframes`
  from { 
    transform: translateY(40px) scale(0.98); 
    opacity: 0; 
  }
  to { 
    transform: translateY(0) scale(1); 
    opacity: 1; 
  }
`;

export const shimmer = keyframes`
  0% {
    transform: translateX(-100%) skewX(-15deg);
  }
  100% {
    transform: translateX(200%) skewX(-15deg);
  }
`;

export const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 4px 15px rgba(255, 107, 53, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 6px 25px rgba(255, 107, 53, 0.6);
  }
`;

export const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

export const glow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 5px rgba(255, 107, 53, 0.3));
  }
  50% {
    filter: drop-shadow(0 0 20px rgba(255, 107, 53, 0.6));
  }
`;

export const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;
  padding: 0rem 2rem 2rem 2rem;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 80%,
        rgba(255, 107, 53, 0.05) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 20%,
        rgba(255, 107, 53, 0.05) 0%,
        transparent 50%
      );
    pointer-events: none;
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Navigation
export const BackNavigation = styled.div`
  display: flex;
  align-items: center;
  animation: ${fadeInUp} 0.6s ease-out;
`;

export const BackLink = styled.div`
  z-index: 100;
  display: flex;
  margin: 20px 0px;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  color: #ff6b35;
  font-weight: bold;
  padding: 7.5px 15px;
  border-radius: 25px;
  background: rgba(26, 26, 26, 0.8);
  border: 1px solid rgba(255, 107, 53, 0.3);
  text-decoration: none;
  width: fit-content;
  transition: 0.3s;
  &:hover {
    background: #ff6b35;
    color: #fff;
    transform: translateX(-5px);
  }
`;

export const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 1rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;

  span {
    color: rgba(255, 255, 255, 0.8);
  }

  .separator {
    color: #ff6b35;
  }

  .current {
    color: #ff6b35;
    font-weight: 600;
  }
`;

// Article Header
export const ArticleHeader = styled.div`
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
  outline: 2px solid rgba(255, 107, 53, 0.3);
  backdrop-filter: blur(15px);
  box-shadow: 0 20px 40px rgba(255, 107, 53, 0.25);
  position: relative;
  overflow: hidden;
  animation: ${slideInDown} 0.8s ease-out;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  background: ${({ $backgroundImage }) =>
    $backgroundImage
      ? `linear-gradient(
          135deg, 
           rgba(26, 26, 26, 0.8) 0%, 
          rgba(42, 42, 42, 0.9) 50%,
          rgba(26, 26, 26, 0.8) 100%
        ), 
        url(${$backgroundImage})`
      : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)"};

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  min-height: ${({ $backgroundImage }) =>
    $backgroundImage ? "400px" : "auto"};

  display: flex;
  flex-direction: column;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #ff8c42);
    border-radius: 20px 20px 0 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    right: -20%;
    width: 400px;
    height: 400px;
    background: radial-gradient(
      circle,
      rgba(255, 107, 53, 0.1) 0%,
      transparent 70%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

export const ArticleCategory = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 107, 53, 0.9);
  color: #ffffff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1.5rem;
  width: fit-content;
  animation: ${pulse} 2s ease-in-out infinite;

  svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
`;

export const ArticleTitle = styled.h1`
  color: #ffffff;
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  background: linear-gradient(
    135deg,
    #ffffff 0%,
    rgba(255, 255, 255, 0.8) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba(255, 255, 255, 0.3);

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

export const ArticleMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.08);
  padding: 8px 12px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 107, 53, 0.3);
    transform: translateY(-2px);
  }

  svg {
    color: #ff6b35;
    filter: drop-shadow(0 2px 4px rgba(255, 107, 53, 0.3));
  }
`;

export const BandTag = styled.span`
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  color: #ffffff;
  padding: 12px 20px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.4);
`;

// Article Content
export const ArticleMain = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const ArticleContent = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-radius: 20px;
  padding: 3rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  animation: ${slideInUp} 0.8s ease-out 0.2s both;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::after {
    content: "";
    position: absolute;
    top: -20%;
    right: -20%;
    width: 60%;
    height: 60%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.05) 0%,
      transparent 70%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    border-color: rgba(255, 107, 53, 0.2);
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

export const ArticleText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  line-height: 1.8;
  font-weight: 300;

  p {
    margin-bottom: 1.5rem;
  }

  h2,
  h3 {
    color: #ffffff;
    margin: 2rem 0 1rem 0;
    font-weight: 600;
  }

  h2 {
    font-size: 1.8rem;
    background: linear-gradient(135deg, #ff6b35, #ff8c42);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  h3 {
    font-size: 1.4rem;
  }

  blockquote {
    background: rgba(255, 107, 53, 0.1);
    border-left: 4px solid #ff6b35;
    padding: 1.5rem;
    margin: 2rem 0;
    border-radius: 0 12px 12px 0;
    font-style: italic;
    backdrop-filter: blur(10px);
  }

  ul,
  ol {
    padding-left: 1.5rem;
    margin-bottom: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

// Empty State Components
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 107, 53, 0.2);
  }
`;

export const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 107, 53, 0.1);
  border-radius: 50%;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 107, 53, 0.2);

  svg {
    color: #ff6b35;
    font-size: 1.5rem;
    opacity: 0.7;
  }
`;

export const EmptyStateText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.95rem;
  margin: 0;
  font-style: italic;
  line-height: 1.4;
  max-width: 250px;
`;

// Sidebar
export const ArticleSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  animation: ${slideInUp} 0.8s ease-out 0.4s both;
`;

export const SidebarCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-radius: 16px;
  padding: 2rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 107, 53, 0.1),
      transparent
    );
    transition: all 0.6s;
  }

  &::after {
    content: "";
    position: absolute;
    top: -20%;
    right: -20%;
    width: 60%;
    height: 60%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.05) 0%,
      transparent 70%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    border-color: rgba(255, 107, 53, 0.3);
    box-shadow: 0 25px 50px rgba(255, 107, 53, 0.2);
  }
`;

export const SidebarTitle = styled.h3`
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  svg {
    color: #ff6b35;
    filter: drop-shadow(0 2px 4px rgba(255, 107, 53, 0.3));
  }
`;

// Social Share
export const SocialShare = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const ShareButton = styled.button`
  background: linear-gradient(
    135deg,
    rgba(255, 107, 53, 0.2),
    rgba(255, 107, 53, 0.1)
  );
  color: #ff6b35;
  border: 1px solid rgba(255, 107, 53, 0.3);
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.2);
  min-width: 48px;
  height: 48px;

  &:hover {
    background: linear-gradient(135deg, #ff6b35, #ff8c42);
    color: #ffffff;
    transform: translateY(-3px) scale(1.1);
    box-shadow: 0 8px 25px rgba(255, 107, 53, 0.4);
    border-color: #ff6b35;
  }

  svg {
    transition: all 0.3s;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

// Related Articles
export const RelatedArticles = styled.div`
  margin-top: 4rem;
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;
`;

export const RelatedTitle = styled.h2`
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;

  svg {
    color: #ff6b35;
    animation: ${float} 3s ease-in-out infinite;
    filter: drop-shadow(0 4px 15px rgba(255, 107, 53, 0.3));
  }
`;

export const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

export const RelatedCard = styled(motion.article)`
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(255, 255, 255, 0.08) 100%
  );
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  backdrop-filter: blur(15px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 107, 53, 0.1),
      transparent
    );
    transition: all 0.6s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 50px rgba(255, 107, 53, 0.2);
    border-color: rgba(255, 107, 53, 0.3);
  }
`;

export const RelatedImage = styled.div`
  height: 150px;
  background: linear-gradient(45deg, #333, #555, #ff6b35);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 107, 53, 0.3),
      rgba(0, 0, 0, 0.6)
    );
  }
`;

export const RelatedContent = styled.div`
  padding: 1.5rem;
`;

export const RelatedCardTitle = styled.h3`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  background: linear-gradient(135deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const RelatedExcerpt = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Tags
export const ArticleTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 2rem;
`;

export const Tag = styled.span`
  background: linear-gradient(
    135deg,
    rgba(255, 107, 53, 0.2),
    rgba(255, 107, 53, 0.1)
  );
  color: #ff6b35;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid rgba(255, 107, 53, 0.3);
  backdrop-filter: blur(10px);
  transition: all 0.3s;

  &:hover {
    background: linear-gradient(135deg, #ff6b35, #ff8c42);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
  }
`;

// Action Buttons
export const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

export const ActionBtn = styled.button`
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg, #ff6b35, #ff8c42)"
      : "linear-gradient(145deg, rgba(255, 107, 53, 0.15), rgba(255, 107, 53, 0.08))"};
  color: ${({ $primary }) => ($primary ? "#ffffff" : "#ff6b35")};
  border: 1px solid rgba(255, 107, 53, 0.3);
  padding: 12px 20px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ $primary }) =>
        $primary ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 107, 53, 0.1)"},
      transparent
    );
    transition: all 0.6s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: linear-gradient(135deg, #ff6b35, #ff8c42);
    color: #ffffff;
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 8px 25px rgba(255, 107, 53, 0.4);
    border-color: #ff6b35;
  }

  svg {
    transition: all 0.3s;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;
